import React, { useEffect, useState } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Chip,
    TextField,
    Button,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { post, get } from "src/services/content.service";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showAlert } from "src/redux/slices/AlertSlice";

export default function GroupSelector({ selectedGroups, setSelectedGroups }) {
    const [allGroups, setAllGroups] = useState([]);
    const [newGroupName, setNewGroupName] = useState("");
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Fetch groups from backend
    const fetchGroups = async () => {
        try {
            const res = await get(
                dispatch,
                `/api/accounts/groups/`
            );
            setAllGroups(res.data);
        } catch (err) {
            console.error("Error fetching groups", err);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleSelectChange = (event) => {
        const value = event.target.value;
        setSelectedGroups(typeof value === "string" ? value.split(",") : value);
    };

    const handleDelete = (group) => {
        setSelectedGroups(selectedGroups.filter((g) => g !== group));
    };

    const handleAddGroup = async () => {
        if (!newGroupName.trim() || allGroups.includes(newGroupName)) return;

        try {
            const res = await post(dispatch, "/api/accounts/groups/", {
                name: newGroupName,
            });

            const newGroup = res.data; // { id, name }

            setAllGroups([...allGroups, newGroup]);
            setSelectedGroups([...selectedGroups, newGroup.id]);
            setNewGroupName("");

        } catch (err) {
            console.error(err);
            dispatch(
                showAlert({
                    message: t('Group.GroupSelector.addGroup.errorMessage'),
                    severity: "error",
                })
            )
            console.error("Failed to add group");
        }
    };

    return (
        <Box>
            <FormControl sx={{ m: 1, width: 300 }}>
                <InputLabel id="group-select-label">{t('Group.GroupSelector.select.label')}</InputLabel>
                <Select
                    labelId="group-select-label"
                    multiple
                    value={selectedGroups}
                    onChange={handleSelectChange}
                    input={<OutlinedInput label={t('Group.GroupSelector.select.placeholder')} />}
                    renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((id) => {
                                const group = allGroups.find((g) => g.id === id);
                                return (
                                    <Chip
                                        key={id}
                                        label={group ? group.name : id}
                                        onDelete={() => handleDelete(id)}
                                        deleteIcon={<CancelIcon />}
                                    />
                                );
                            })}
                        </Box>
                    )}
                >
                    {allGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                            {group.name}
                        </MenuItem>
                    ))}
                </Select>

            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <TextField
                    label={t('Group.GroupSelector.addGroup.label')}
                    variant="outlined"
                    size="small"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                />
                <Button
                    variant="contained"
                    sx={{ ml: 1 }}
                    onClick={handleAddGroup}
                    disabled={!newGroupName.trim()}
                >
                    {t('buttonCommons.add')}
                </Button>
            </Box>
        </Box>
    );
}
